'use client';
import { Loader, Space } from '@mantine/core';
import { RequestMessage } from '@scaleup/models';
import Image from 'next/image';
import { useState } from 'react';
import { useAlert } from '../hooks';
import { OrgAvatar } from './OrgAvatar';

interface ChatModuleProps {
  messages: RequestMessage[];
  loading: boolean;
  handleSendMessage: (arg: string) => Promise<void>;
  rightOrgId: string;
  leftOrgId: string;
}

export const ChatModule = ({
  messages,
  loading,
  handleSendMessage,
  rightOrgId,
  leftOrgId,
}: ChatModuleProps) => {
  const [message, setMessage] = useState<string>('');
  const [messageLoading, setMessageLoading] = useState(false);
  const setAlert = useAlert();

  return (
    <div className="flex bg-white rounded-[2rem] flex flex-col">
      <div className="flex flex-col gap-2 items-center justify-center pt-8 overflow-auto">
        <Image
          src="/icons/clapping-hand.svg"
          alt="Clapping Hands"
          width={24}
          height={24}
        />
        <span className="text-2xl font-bold text-text-primary">
          Welcome to your new partnership!
        </span>
        <span className="w-2/3 text-center">
          Start communicating here to discuss collaboration opportunities and
          details. Let&apos;s make this a successful journey together!
        </span>
        {messages?.map((message) =>
          message.orgId === rightOrgId ? (
            <div
              key={message.messageId}
              className="flex w-full px-4 gap-2 flex-row-reverse items-end"
            >
              <OrgAvatar orgId={rightOrgId} />
              <span className="bg-primary-25 rounded-2xl rounded-br-md p-4">
                {message.message}
              </span>
            </div>
          ) : (
            <div
              key={message.messageId}
              className="flex w-full px-4 gap-2 items-end"
            >
              <OrgAvatar orgId={leftOrgId} />
              <span className="bg-primary-100 rounded-2xl rounded-bl-md p-4">
                {message.message}
              </span>
            </div>
          )
        )}
        <Space h={6} />
      </div>
      <div className="rounded-b-[32px] bg-gradient-to-r from-[#62C393BF] to-[#9ED5E5BF] p-3">
        <div className="flex justify-between gap-2 p-4 items-center bg-white/65 rounded-[2rem]">
          <input
            type="text"
            placeholder="Type here..."
            className="bg-transparent flex-1 p-3 focus:outline-none"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          {messageLoading ? (
            <Loader className="text-primary-600" />
          ) : (
            <button
              className="flex gap-2 shadow-button rounded-[2rem] p-3"
              onClick={async () => {
                try {
                  setMessageLoading(true);
                  await handleSendMessage(message);
                  setMessage('');
                } catch {
                  setAlert({
                    title: 'Something went wrong sending your message',
                    message: 'Please try again in a moment',
                    severity: 'warning',
                    show: true,
                  });
                } finally {
                  setMessageLoading(false);
                }
              }}
              disabled={loading || message === ''}
            >
              Send
              <Image src="/icons/send.svg" alt="Send" width={24} height={24} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
